import type { Configuration, RedirectRequest } from '@azure/msal-browser';

interface ExternalIdPolicies {
  signUpSignIn: {
    name: string;
    authority: string;
  };
}

export interface ExternalIdConfiguration {
  msalConfig: Configuration;
  loginRequest: RedirectRequest;
  externalIdPolicies: ExternalIdPolicies;
  authority: {
    url: string;
    host: string;
  };
  tenantId: string;
}

function normalize(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
}

function parseScopes(rawScopes: string | undefined): string[] | undefined {
  if (!rawScopes) {
    return undefined;
  }

  const scopes = rawScopes
    .split(/[\,\s]+/)
    .map((scope) => scope.trim())
    .filter((scope) => scope.length > 0);

  return scopes.length > 0 ? scopes : undefined;
}

function sanitizePathSegment(segment: string): string | undefined {
  const sanitized = segment.replace(/^[\/]+|[\/]+$/g, '').trim();
  return sanitized.length > 0 ? sanitized : undefined;
}

function sanitizeAuthorityPath(path: string): string | undefined {
  const segments = path
    .split('/')
    .map((segment) => segment.trim())
    .filter((segment) => segment.length > 0);

  if (segments.length === 0) {
    return undefined;
  }

  return segments.join('/');
}

function parseBoolean(value: string | undefined): boolean | undefined {
  if (!value) {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();

  if (normalized === 'true' || normalized === '1' || normalized === 'yes') {
    return true;
  }

  if (normalized === 'false' || normalized === '0' || normalized === 'no') {
    return false;
  }

  return undefined;
}

export class ExternalIdConfigurationError extends Error {
  constructor(public readonly missing: string[]) {
    const details = missing.map((key) => `- ${key}`).join('\n');
    super(
      [
        missing.length === 1
          ? 'Missing required environment variable:'
          : 'Missing required environment variables:',
        details,
        'Configure the Microsoft Entra External ID credentials in a .env file or Static Web Apps application setting.'
      ]
        .filter(Boolean)
        .join('\n')
    );
    this.name = 'ExternalIdConfigurationError';
  }
}

function sanitizeAuthorityDomain(rawAuthority: string): string | undefined {
  const sanitized = rawAuthority
    .replace(/^https?:\/\//i, '')
    .split('/')[0]
    .trim()
    .toLowerCase();

  return sanitized.length > 0 ? sanitized : undefined;
}

function sanitizeAuthorityUrl(rawAuthority: string): URL | null {
  try {
    const normalized = /^https?:\/\//i.test(rawAuthority) ? rawAuthority : `https://${rawAuthority}`;
    const url = new URL(normalized);

    if (url.protocol !== 'https:') {
      return null;
    }

    if (url.search || url.hash) {
      return null;
    }

    // Ensure the authority includes a path segment (for the user flow) and strip trailing slashes.
    const cleanedPath = url.pathname.replace(/\/+$/, '');

    if (!cleanedPath || cleanedPath === '') {
      return null;
    }

    url.pathname = cleanedPath;
    return url;
  } catch (error) {
    console.error('Invalid Microsoft Entra External ID authority URL provided.', error);
    return null;
  }
}

export function loadExternalIdConfiguration(): ExternalIdConfiguration {
  const missing: string[] = [];

  const tenantName = normalize(import.meta.env.VITE_ENTRA_EXTERNAL_ID_TENANT_NAME);
  const tenantId = normalize(import.meta.env.VITE_ENTRA_EXTERNAL_ID_TENANT_ID);
  const signInUserFlow = normalize(import.meta.env.VITE_ENTRA_EXTERNAL_ID_SIGNIN_USER_FLOW);
  const clientId = normalize(import.meta.env.VITE_ENTRA_EXTERNAL_ID_CLIENT_ID);
  const apiScopes = parseScopes(normalize(import.meta.env.VITE_ENTRA_EXTERNAL_ID_API_SCOPE));
  const authorityDomainRaw = normalize(import.meta.env.VITE_ENTRA_EXTERNAL_ID_AUTHORITY_DOMAIN);
  const authorityUrlRaw = normalize(import.meta.env.VITE_ENTRA_EXTERNAL_ID_AUTHORITY_URL);
  const tenantDomainOverride = normalize(import.meta.env.VITE_ENTRA_EXTERNAL_ID_TENANT_DOMAIN);
  const skipTenantDomainPathRaw = normalize(
    import.meta.env.VITE_ENTRA_EXTERNAL_ID_SKIP_TENANT_DOMAIN_PATH
  );
  const skipTenantDomainPath = parseBoolean(skipTenantDomainPathRaw) ?? false;
  const authorityPathRaw = normalize(import.meta.env.VITE_ENTRA_EXTERNAL_ID_AUTHORITY_PATH);
  const authorityPathOverride = authorityPathRaw ? sanitizeAuthorityPath(authorityPathRaw) : undefined;

  let authorityDomain: string | undefined;
  let authorityUrl: URL | null = null;

  if (!tenantName) {
    missing.push('VITE_ENTRA_EXTERNAL_ID_TENANT_NAME');
  }

  if (!tenantId) {
    missing.push('VITE_ENTRA_EXTERNAL_ID_TENANT_ID');
  }

  if (!signInUserFlow && !authorityPathOverride && skipTenantDomainPath) {
    missing.push('VITE_ENTRA_EXTERNAL_ID_SIGNIN_USER_FLOW');
  }

  if (!clientId) {
    missing.push('VITE_ENTRA_EXTERNAL_ID_CLIENT_ID');
  }

  if (!apiScopes) {
    missing.push('VITE_ENTRA_EXTERNAL_ID_API_SCOPE');
  }

  if (authorityUrlRaw) {
    authorityUrl = sanitizeAuthorityUrl(authorityUrlRaw);

    if (!authorityUrl) {
      missing.push('VITE_ENTRA_EXTERNAL_ID_AUTHORITY_URL (must be a valid https:// URL that includes your user flow path)');
    }
  }

  if (!authorityUrl) {
    if (!authorityDomainRaw) {
      missing.push('VITE_ENTRA_EXTERNAL_ID_AUTHORITY_DOMAIN');
    } else {
      authorityDomain = sanitizeAuthorityDomain(authorityDomainRaw);

      if (!authorityDomain) {
        missing.push('VITE_ENTRA_EXTERNAL_ID_AUTHORITY_DOMAIN');
      }
    }
  }

  if (authorityPathRaw && !authorityPathOverride) {
    missing.push(
      'VITE_ENTRA_EXTERNAL_ID_AUTHORITY_PATH (must include at least one non-empty path segment)'
    );
  }

  if (missing.length > 0) {
    throw new ExternalIdConfigurationError(missing);
  }

  const resolvedTenantId = tenantId as string;
  const resolvedSignInUserFlow = signInUserFlow ?? '';
  const resolvedClientId = clientId as string;
  const resolvedApiScopes = apiScopes as string[];

  const redirectUri = normalize(import.meta.env.VITE_ENTRA_EXTERNAL_ID_REDIRECT_URI) ?? window.location.origin;
  const postLogoutRedirectUri =
    normalize(import.meta.env.VITE_ENTRA_EXTERNAL_ID_POST_LOGOUT_REDIRECT_URI) ?? window.location.origin;

  const defaultTenantDomainSegment = sanitizePathSegment(`${tenantName as string}.onmicrosoft.com`);
  const sanitizedTenantDomain = sanitizePathSegment(tenantDomainOverride ?? defaultTenantDomainSegment ?? '');
  const sanitizedSignInUserFlow = sanitizePathSegment(resolvedSignInUserFlow);

  const authorityPathFromSegments = (() => {
    if (authorityPathOverride) {
      return authorityPathOverride;
    }

    if (skipTenantDomainPath) {
      if (!sanitizedSignInUserFlow) {
        throw new ExternalIdConfigurationError([
          'VITE_ENTRA_EXTERNAL_ID_SIGNIN_USER_FLOW (must include a valid user flow name)'
        ]);
      }

      return sanitizedSignInUserFlow;
    }

    if (!sanitizedTenantDomain) {
      throw new ExternalIdConfigurationError([
        'VITE_ENTRA_EXTERNAL_ID_TENANT_NAME (must produce a valid tenant domain path segment)'
      ]);
    }

    return sanitizedTenantDomain;
  })();

  const resolvedAuthority = authorityUrl
    ? `${authorityUrl.origin}${authorityUrl.pathname}`
    : `https://${authorityDomain as string}/${authorityPathFromSegments}`;

  const resolvedKnownAuthority = authorityUrl ? authorityUrl.host : (authorityDomain as string);

  const policyName = sanitizedSignInUserFlow
    ? sanitizedSignInUserFlow
    : sanitizedTenantDomain ?? authorityPathFromSegments.split('/').pop() ?? authorityPathFromSegments;

  const externalIdPolicies: ExternalIdPolicies = {
    signUpSignIn: {
      name: policyName,
      authority: resolvedAuthority
    }
  };

  const authorityDetails = authorityUrl
    ? { url: resolvedAuthority, host: authorityUrl.host }
    : { url: resolvedAuthority, host: authorityDomain as string };

  return {
    externalIdPolicies,
    msalConfig: {
      auth: {
        clientId: resolvedClientId,
        authority: externalIdPolicies.signUpSignIn.authority,
        knownAuthorities: [resolvedKnownAuthority],
        redirectUri,
        postLogoutRedirectUri
      },
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false
      }
    },
    loginRequest: {
      scopes: resolvedApiScopes
    },
    authority: authorityDetails,
    tenantId: resolvedTenantId
  };
}
